import styled from 'styled-components';

import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner } from '@creditornot/cb-progress';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { DataTable, DataTableProps } from 'common/components/cookbook/DataTable/DataTable';
import { SelectionState } from 'common/components/cookbook/DataTable/types';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { media } from 'common/media';
import { AxiosPromotionRecommendationsError } from 'common/types';
import { formatMoney } from 'common/utils/moneyUtils';
import { formatNumber } from 'common/utils/numberUtils';
import { ProductCard } from 'features/selection-recommendations/common/ProductCard';
import { RecommendationReasons } from 'features/selection-recommendations/common/RecommendationReasons/RecommendationReasons';
import { Vendors } from 'features/selection-recommendations/common/Vendors/Vendors';
import { getListingDuration, getPriceRange } from 'features/selection-recommendations/utils';

import { PromotionRecommendationSubList } from './PromotionRecommendationSubList';
import { PromotionRecommendationItem } from './types';

const EmptyStateContainer = styled(ContentContainer)`
  padding: ${dimension.spaceBroadMedium};
`;

const SpinnerContainer = styled(ContentContainer)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const StyledDataTable = styled(DataTable as React.FC<DataTableProps<PromotionRecommendationItem>>)`
  @media ${media.lg} {
    [role='cell']:nth-child(2),
    [role='columnheader']:nth-child(2) {
      border-right: 1px solid ${color.border};
      left: 48px;
      position: sticky;
      z-index: 0;
    }
  }
`;

type Props = {
  error: AxiosPromotionRecommendationsError | null;
  dataSource: PromotionRecommendationItem[];
  fetchNextPage: () => void;
  rowSelection: {
    onRowSelectChange: (value: 'ALL' | SelectionState) => void;
    selectedRows: string[] | 'ALL';
  };
  hasNextPage: boolean;
  totalCount: number | undefined;
  isFetching: boolean;
  isLoading: boolean;
};

export const PromotionRecommendationsList = ({
  error,
  dataSource,
  rowSelection,
  hasNextPage,
  fetchNextPage,
  totalCount,
  isFetching,
  isLoading,
}: Props) => {
  const locale = useUserLocale();
  const f = useFormatMessage();

  if (error) {
    return (
      <ContentContainer>
        <ApiErrorNotification error={error} />
      </ContentContainer>
    );
  }

  if (!dataSource.length && !isLoading) {
    return (
      <EmptyStateContainer>
        <Empty
          title={f('selection_recommendations.no_recommendations')}
          description={f('selection_recommendations.no_recommendations_description')}
        />
      </EmptyStateContainer>
    );
  }

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  return (
    <StyledDataTable
      dataSource={dataSource}
      isStickyHeader={true}
      dataTestId="promotion-recommendations-list"
      stickyHeaderOffset={65}
      hideScrollbars={true}
      rowSelection={rowSelection}
      renderSubComponent={(record) => {
        return <PromotionRecommendationSubList parent={record} />;
      }}
      fetchOnScroll={{
        hasNextPage,
        onFetchMore: fetchNextPage,
        isFetching,
      }}
      columns={[
        {
          id: 'product',
          header: f('selection_recommendations.recommendations_count', {
            count: formatNumber(locale, totalCount ?? 0),
          }),
          width: 400,
          alignNameVertical: 'middle',
          render: ({ record: { product } }) => {
            return (
              <ProductCard
                productId={product.id}
                imgSrc={product.image?.url}
                productName={product.name}
                productGtin={product.gtin ?? undefined}
                productCode={product.code}
                landingPage="promotions"
                currentTab="PROMOTION_RECOMMENDATIONS"
              />
            );
          },
        },
        {
          id: 'expectedBusinessImpact',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          info: f(
            'selection_recommendations.promotion_recommendation_expected_business_impact_column_explanation',
          ),
          width: 150,
          header: f('selection_recommendations.expected_business_impact'),
          render: ({ record: { expectedBusinessImpact } }) => {
            return expectedBusinessImpact?.revenueImpact?.amount !== undefined
              ? formatMoney(locale, expectedBusinessImpact.revenueImpact)
              : '-';
          },
        },
        {
          id: 'reasons',
          alignNameVertical: 'middle',
          header: f('selection_recommendations.reason'),
          render: ({ record: { reasons } }) => {
            return <RecommendationReasons reasons={reasons} />;
          },
        },
        {
          id: 'lastPromotion',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 120,
          header: f('selection_recommendations.last_promotion'),
          render: ({ record: { lastPromotionEndedAt } }) => {
            if (!lastPromotionEndedAt) return '-';
            const listingDuration = getListingDuration(lastPromotionEndedAt, locale);
            return f('selection_recommendations.last_promotion_ago', {
              count: listingDuration,
            });
          },
        },
        {
          id: 'promoShare',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 120,
          header: f('selection_recommendations.promo_share'),
          render: ({ record: { promoShare } }) => {
            if (!promoShare) return '-';
            return `${formatNumber(locale, promoShare)}%`;
          },
        },
        {
          id: 'salesPrice',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.sales_price_including_vat'),
          render: ({ record: { salesPriceRange } }) => {
            return getPriceRange(salesPriceRange, locale);
          },
        },
        {
          id: 'soldUnits',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.sold_units'),
          render: ({ record: { soldUnitsPast90Days } }) => {
            if (!soldUnitsPast90Days) return '-';
            return formatNumber(locale, soldUnitsPast90Days);
          },
        },
        {
          id: 'costPrice',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.cost_price_excluding_vat'),
          render: ({ record: { costPriceRange } }) => {
            if (!costPriceRange) return '-';
            return getPriceRange(costPriceRange, locale);
          },
        },
        {
          id: 'ppm',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          header: f('selection_recommendations.ppm_percentage'),
          render: ({ record: { pureProfitMargin } }) => {
            if (!pureProfitMargin) return '-';

            return `${formatNumber(locale, pureProfitMargin, 2)}%`;
          },
        },
        {
          id: 'ppmGap',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 150,
          info: f('selection_recommendations.recommendation_ppm_gap_column_explanation'),
          header: (
            <>
              <div>{f('selection_recommendations.ppm_delta')}</div>
              <div>{f('selection_recommendations.ppm_delta_explanation')}</div>
            </>
          ),
          render: ({ record: { pureProfitMarginGapBps } }) => {
            const ppmGap = formatNumber(locale, pureProfitMarginGapBps);

            return pureProfitMarginGapBps && pureProfitMarginGapBps > 0
              ? `+${ppmGap}`
              : ppmGap || '-';
          },
        },
        {
          id: 'priceIndex',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 120,
          info: f('selection_recommendations.recommendation_price_index_column_explanation'),
          header: f('selection_recommendations.price_index'),
          render: ({ record: { priceIndex } }) => {
            if (!priceIndex) return '-';
            return formatNumber(locale, Number(priceIndex), 2);
          },
        },
        {
          id: 'stockCount',
          alignContent: 'right',
          alignName: 'right',
          alignNameVertical: 'middle',
          width: 120,
          header: f('selection_recommendations.stock_count'),
          render: ({ record: { stockCount } }) => {
            if (!stockCount) return '-';
            return formatNumber(locale, stockCount);
          },
        },
        {
          id: 'vendors',
          alignNameVertical: 'middle',
          width: 200,
          header: f('selection_recommendations.vendor'),
          info: f('selection_recommendations.recommendation_vendor_column_explanation'),
          render: ({ record: { vendors } }) => {
            if (!vendors) return '-';
            return <Vendors items={vendors} />;
          },
        },
        {
          id: 'basketPenetration',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 160,
          header: f('selection_recommendations.basket_penetration'),
          render: ({ record: { basketPenetrationPercentage } }) => {
            return basketPenetrationPercentage
              ? `${formatNumber(locale, basketPenetrationPercentage, 2)}%`
              : '-';
          },
        },
        {
          id: 'basketPenetrationPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 160,
          header: f('selection_recommendations.basket_penetration_phl2_median'),
          render: ({ record: { basketPenetrationPercentagePHL2Median } }) => {
            return basketPenetrationPercentagePHL2Median
              ? `${formatNumber(locale, basketPenetrationPercentagePHL2Median, 2)}%`
              : '-';
          },
        },
        {
          id: 'profitPerUnit',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 140,
          header: f('selection_recommendations.profit_per_unit'),
          render: ({ record: { profitPerUnit } }) => {
            if (!profitPerUnit) return '-';
            return formatMoney(locale, profitPerUnit);
          },
        },
        {
          id: 'profitPerUnitPHL2Median',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          width: 140,
          header: f('selection_recommendations.profit_per_unit_phl2_median'),
          render: ({ record: { profitPerUnitPHL2Median } }) => {
            if (!profitPerUnitPHL2Median) return '-';
            return formatMoney(locale, profitPerUnitPHL2Median);
          },
        },
        {
          id: 'revenue',
          alignNameVertical: 'middle',
          alignContent: 'right',
          alignName: 'right',
          header: f('selection_recommendations.revenue'),
          render: ({ record: { totalRevenue } }) => {
            if (!totalRevenue) return '-';
            return formatMoney(locale, totalRevenue);
          },
        },
      ]}
    />
  );
};
